import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { toast } from "@grandvisionhq/common";
import { Types } from "@grandvisionhq/graphql";
import { useAdyen, PaymentServiceProvider, PaymentMethod, Hooks } from "@grandvisionhq/payments";
import { useIntl, useFeatureToggles, useSelector, useDispatch } from "@grandvisionhq/state";
import { useSessionStorage, convertCentsToDecimalString } from "@grandvisionhq/utils";
import { updateCartState } from "../../../../../../../../actions.js";
import { useSetCustomerDetails } from "../../../../../../../../hooks/operations/hooks/operations/use-set-customer-details.js";
import { useCreateOrder } from "../../../../../../../../hooks/orders/hooks/orders/use-create-order.js";
import { useCreateOrderDraft } from "../../../../../../../../hooks/orders/hooks/orders/use-create-order-draft.js";
import { useRedirectToExpressCheckout } from "../../../../../../../../hooks/orders/hooks/orders/use-redirect-to-express-checkout.js";
import { useRedirectToOrderComplete } from "../../../../../../../../hooks/orders/hooks/orders/use-redirect-to-order-confirm-page.js";
import { GooglePaySessionErrors } from "../../../../../../../../mappers/mappers/errors-mapping.js";
import { getChangedOptIns } from "../../../../../../../../selectors.js";
import { getBrowserInfo } from "../../../../../../../../StepPayment/utils/StepPayment/utils/get-browser-info.js";
import { resetExpressCheckout } from "../../../../../../../../StepShipping/StepShipping/actions.js";
import { getShippingDetails } from "../../../../../../../../StepShipping/StepShipping/selectors.js";
import { mapGooglePayContactAddress } from "../../../../mappers/modules/ExpressCheckout/hooks/google-pay/mappers/google-pay-contact-address.js";
import { getDateOfBirth } from "../../../../mappers/modules/ExpressCheckout/hooks/google-pay/mappers/google-pay-date-of-birth.js";
import { useMapShippingAddress } from "../../../../utility-hooks/modules/ExpressCheckout/hooks/google-pay/utility-hooks/use-map-shipping-address.js";
import { useValidatePaymentData } from "../../../../utility-hooks/modules/ExpressCheckout/hooks/google-pay/utility-hooks/use-validate-payment-data.js";
import { ShippingMethod } from "../../../../../../../../models/step-shipping/models/step-shipping/shipping-methods.js";
import { getSelectedOptIns } from "../../../../../../../../utils/utils/get-selected-opt-ins.js";
const useGooglePay = ({ selfUrl, returnPath } = { selfUrl: "" }) => {
  const { locale, currency, country } = useIntl();
  const { featureConfigs, allowAlternativeShippingState } = useFeatureToggles();
  const redirectToOrderComplete = useRedirectToOrderComplete();
  const redirectToExpressCheckout = useRedirectToExpressCheckout();
  const [error, setError] = React.useState();
  const shippingDetails = useSelector(getShippingDetails);
  const changedOptIns = useSelector(getChangedOptIns);
  const { cart } = Context.useCart();
  const mapShippingAddress = useMapShippingAddress();
  const dispatch = useDispatch();
  const validatePaymentData = useValidatePaymentData();
  const [paymentConfiguration] = useSessionStorage(
    "payment-configuration"
  );
  const shippingAddressRequired = React.useMemo(
    () => cart?.shippingMethod.key === ShippingMethod.Delivery || cart?.shippingMethod.key === ShippingMethod.DeliveryExpress || cart?.shippingMethod.key === ShippingMethod.DeliveryPremium,
    [cart?.shippingMethod.key]
  );
  const createOrderDraft = useCreateOrderDraft();
  const [createOrder, { error: createOrderError, loading: createOrderLoading }] = useCreateOrder({
    returnPath
  });
  const { AdyenModule, clientKey, environment, config } = useAdyen();
  const addressValidation = config?.googlepay?.addressValidation;
  const isShippingAddressValid = React.useCallback(
    (address) => {
      let isAddressValid = true;
      if (addressValidation?.streetName && !addressValidation?.streetName(address?.streetName || "")) {
        toast.failed("checkout.address.streetName.errorMessage");
        isAddressValid = false;
      }
      if (addressValidation?.fullName && !addressValidation?.fullName(address?.firstName + " " + address?.lastName || "")) {
        toast.failed("checkout.address.fullName.errorMessage");
        isAddressValid = false;
      }
      if (address?.additionalStreetInfo && addressValidation?.additionalStreetInfo && !addressValidation?.additionalStreetInfo(address?.additionalStreetInfo || "")) {
        toast.failed("checkout.address.additionalStreetInfo.errorMessage");
        isAddressValid = false;
      }
      if (address?.additionalAddressInfo && addressValidation?.additionalStreetInfo && !addressValidation?.additionalStreetInfo(address?.additionalAddressInfo || "")) {
        toast.failed("checkout.address.additionalStreetInfo.errorMessage");
        isAddressValid = false;
      }
      return isAddressValid;
    },
    [addressValidation]
  );
  const onSetCustomerDetailsCompleted = React.useCallback(() => {
    cart?.checkoutState && dispatch(
      updateCartState({
        cartState: cart?.checkoutState
      })
    );
  }, [cart?.checkoutState, dispatch]);
  const { setCustomerDetails, updateError } = useSetCustomerDetails({
    onCompleted: onSetCustomerDetailsCompleted
  });
  const getCustomerDetails = React.useCallback(
    (paymentData, shippingMethod) => {
      const dateOfBirth = getDateOfBirth(shippingDetails?.expressCheckout);
      const phone = paymentData.paymentMethodData.info?.billingAddress?.phoneNumber;
      const vatId = shippingDetails?.expressCheckout?.formData?.customerVatId?.value;
      let selectedOptIns = [];
      if (changedOptIns) {
        selectedOptIns = getSelectedOptIns(changedOptIns);
      }
      return {
        customerDetails: {
          customerType: Types.CheckoutCustomerType.B2C,
          email: paymentData.email,
          ...dateOfBirth ? { dateOfBirth } : {},
          ...phone ? { phone } : {}
        },
        billingAddress: mapGooglePayContactAddress(paymentData, "billingAddress"),
        shippingAddress: mapShippingAddress(paymentData, shippingMethod),
        ...vatId ? { invoiceDetails: { vatId } } : {},
        marketingEmailsOptIn: selectedOptIns.includes("marketingEmailsOptIn")
      };
    },
    [changedOptIns, mapShippingAddress, shippingDetails?.expressCheckout]
  );
  const options = React.useMemo(
    () => ({
      clientKey,
      environment,
      locale: `${locale}`
    }),
    [clientKey, environment, locale]
  );
  const checkout = React.useMemo(() => {
    const AdyenCheckout = AdyenModule?.result;
    return AdyenCheckout && AdyenCheckout(options);
  }, [AdyenModule, options]);
  const isPostalCodeServiceable = React.useCallback(
    (postalCode) => {
      if (!postalCode) return false;
      if (allowAlternativeShippingState && featureConfigs.alternativeShippingRegex) {
        return !featureConfigs.alternativeShippingRegex.test(postalCode);
      }
      return true;
    },
    [featureConfigs.alternativeShippingRegex, allowAlternativeShippingState]
  );
  const onAuthorized = React.useCallback(
    async (paymentData) => {
      const { isPaymentDataValid, error: error2 } = validatePaymentData(paymentData);
      if (!isPaymentDataValid && error2) {
        setError(
          "INVALID_SHIPPING"
          /* INVALID_SHIPPING */
        );
        toast.failed(error2.message);
        return;
      }
      if (!cart?.shippingMethod) {
        setError(
          "INVALID_SHIPPING"
          /* INVALID_SHIPPING */
        );
        console.error("Shipping method is not selected");
        return;
      }
      const selectedShippingMethod = cart.shippingMethod.key;
      const customerDetails = getCustomerDetails(paymentData, selectedShippingMethod);
      if (!isShippingAddressValid(customerDetails.shippingAddress)) {
        setError(
          "INVALID_SHIPPING"
          /* INVALID_SHIPPING */
        );
        return;
      }
      if (!isPostalCodeServiceable(customerDetails.shippingAddress?.postalCode)) {
        setError(
          "INVALID_SHIPPING"
          /* INVALID_SHIPPING */
        );
        toast.failed("checkout.address.postalCode.unavailable.errorMessage");
        return;
      }
      await setCustomerDetails(customerDetails);
      if (updateError) {
        toast.failed("checkout.errors.general.message");
      } else {
        const googlePayToken = paymentData.paymentMethodData.tokenizationData.token;
        const createOrderDraftPayload = await createOrderDraft({
          paymentDetails: {
            data: { googlePayToken, browserInfo: { ...getBrowserInfo() } },
            isValid: true,
            type: PaymentMethod.GOOGLE_PAY
          },
          selectedPaymentMethod: {
            type: PaymentMethod.GOOGLE_PAY,
            provider: PaymentServiceProvider.ADYEN
          },
          returnUrl: `${selfUrl}/api/payment-callback?type=GOOGLE_PAY&checkoutType=express`
        });
        const orderResponse = await createOrder(createOrderDraftPayload);
        dispatch(resetExpressCheckout());
        if (orderResponse?.success) {
          redirectToOrderComplete({
            cartId: orderResponse?.cartId || createOrderDraftPayload.checkoutId,
            orderData: orderResponse?.orderDataToken,
            checkoutMethod: "express_checkout_googlepay"
          });
        } else {
          orderResponse?.cartId && redirectToExpressCheckout({
            type: "GOOGLE_PAY",
            cartId: orderResponse.cartId
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changedOptIns, shippingDetails, isPostalCodeServiceable, cart]
  );
  const googlePayConfigProps = React.useMemo(
    () => ({
      locale,
      cartPrices: cart?.prices,
      lineItems: (cart?.lineItems ?? []).map((lineItem) => ({
        name: lineItem.name,
        price: convertCentsToDecimalString(lineItem.price.unit.gross)
      })),
      currency,
      country,
      onAuthorized
    }),
    [cart, country, currency, locale, onAuthorized]
  );
  const { expressGooglePayConfig } = Hooks.useGooglePayConfig(googlePayConfigProps);
  const handleGooglePayPayment = React.useCallback(
    async (config2) => {
      if (!checkout) {
        return;
      }
      try {
        const googlePayCheckout = await checkout;
        if (!googlePayCheckout) {
          console.error("Checkout object is not initialized.");
          return;
        }
        const googlePayComponent = googlePayCheckout.create("googlepay", config2);
        if (!googlePayComponent) {
          console.error("Failed to create Google Pay component.");
          return;
        }
        const { result } = await googlePayComponent.isReadyToPay();
        if (result) {
          googlePayComponent.submit();
        } else {
          console.warn("Google Pay is not available.");
        }
      } catch (error2) {
        console.error("Error during Google Pay process:", error2);
      }
    },
    [checkout]
  );
  const payCartWithGooglePay = React.useCallback(() => {
    if (error && error !== "UNSUPPORTED") {
      console.error("Google pay is not supported.", error);
      setError(
        "UNSUPPORTED"
        /* UNSUPPORTED */
      );
      toast.failed(GooglePaySessionErrors.GooglePaySessionUnsupported);
      return;
    }
    const { merchantId, gatewayMerchantId } = paymentConfiguration ?? {};
    const config2 = expressGooglePayConfig(merchantId, gatewayMerchantId, shippingAddressRequired);
    handleGooglePayPayment(config2).catch(
      (error2) => console.error("Error loading Google Pay", error2)
    );
  }, [
    error,
    expressGooglePayConfig,
    paymentConfiguration,
    handleGooglePayPayment,
    shippingAddressRequired
  ]);
  return [
    payCartWithGooglePay,
    {
      error: error || createOrderError,
      loading: createOrderLoading
    }
  ];
};
export {
  useGooglePay
};
//# sourceMappingURL=use-google-pay.js.map
